import React, { useCallback, useEffect, useState, useRef } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import InboxIcon from "@mui/icons-material/Inbox";
import { Box, Typography } from "@mui/material";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { fetchWorkshiftRotationAssignment } from "../../../Apis/Workshift-api";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_COLUMNS = [
  {
    field: "employee_name",
    label: "Employee Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "employee_code",
    label: "Employee Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "rotation_pattern",
    label: "Rotation Pattern",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "effective_start_date",
    label: "Start Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "effective_end_date",
    label: "End Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "anchor_day_number",
    label: "Anchor Day",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "rotation_pattern",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function WorkshiftRotationAssignmentList() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const org = userData?.organization;

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `employee_work_shift_rotation_assignments_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // load data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "rotation_pattern",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/workshift-rotation-assignment/filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchWorkshiftRotationAssignment(org.organization_id);
          data = oldData?.data || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data to match table columns (API returns employee_name, rotation_pattern, etc.)
        let b = (data || []).map((item) => {
          const name = item.employee_name ?? (item?.employee
            ? `${item.employee?.first_name ?? ""} ${item.employee?.middle_name ?? ""} ${item.employee?.last_name ?? ""}`.trim()
            : "");
          // Support multiple possible keys from API / saved config so "Work Shift Rotation Pattern" column always has a value
          const rawPattern =
            item.rotation_pattern ??
            item.rotation_pattern_obj?.pattern_name ??
            item.work_shift_rotation_pattern ??
            item.pattern_name ??
            item.workshift_pattern?.pattern_name;
          const pattern = rawPattern != null && rawPattern !== "" ? String(rawPattern).trim() : "";
          return {
            ...item,
            id: item.employee_work_shift_rotation_assignment_id,
            employee_code: item.employee_code ?? item?.employee?.employee_code ?? "",
            employee_name: name,
            effective_start_date: item.effective_start_date ?? "",
            effective_end_date: item.effective_end_date ?? "",
            remarks: item.remarks ?? "",
            rotation_pattern: pattern,
            work_shift_rotation_pattern: pattern,
            pattern_name: pattern,
            is_active: item.is_active == 1 || item.is_active === true ? "✔" : "✖",
          };
        });
        setShifts(b);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch rotation assignments:", err);
          toast.error("Failed to fetch rotation assignments");
          setShifts([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/workshift-rotation-assignment/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Rotation assignment deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const status = response.status;
          if (status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else if (status === 422) {
            const firstError =
              data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
              "Validation failed.";
            toast.error(firstError);
          } else if (status === 409) {
            toast.error(data.error || "This assignment is linked to other records.");
          } else {
            toast.error(data.message || data.error || "Failed to delete assignment");
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete rotation assignment"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/work-shift-rotation-assignment/edit/${item.id || item.employee_work_shift_rotation_assignment_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/work-shift-rotation-assignment/view/${item.id || item.employee_work_shift_rotation_assignment_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"WorkShift Rotation Assignments"}
        btnName={"Add WorkShift"}
        delete_action={"SHIFT_ROTATION_ASSIGNMENT_DELETE"}
        Data={shifts}
        add_action={"SHIFT_ROTATION_ASSIGNMENT_ADD"}
        tableHeaders={[
          {
            name: "Employee Name",
            value_key: "employee_name",
            textStyle: "capitalize",
          },
          { name: "Workshift Pattern", value_key: "rotation_pattern" },
          { name: "Start date", value_key: "effective_start_date" },
          { name: "End date", value_key: "effective_end_date" },
          { name: "Anchor Day No.", value_key: "anchor_day_number" },
          { name: "Remarks", value_key: "remarks", textStyle: "capitalize" },
        ]}
        Icons={[
          <FormatAlignJustifyIcon
            sx={{ fontSize: 60, color: "grey.500", mb: 2 }}
          />,
          <FormatAlignJustifyIcon color="primary" />,
          <NextWeekIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "WorkShift Rotation Assignments",
          "WorkShifts Rotation Assignments",
          "Add Rotation",
          "WorkShift Rotation Assignments",
        ]}
        Route={"/organization/work-shift-rotation-assignment"}
        setData={setShifts}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
            tableName="employee_work_shift_rotation_assignments"
            primaryKey="employee_work_shift_rotation_assignment_id"
            heading="WorkShift Rotation Assignments"
            data={shifts}
            sortname="employee_name"
            showActions={true}
            Route="/organization/work-shift-rotation-assignment"
            DeleteFunc={handleDelete}
            handleShow={handleShow}
            EditFunc={handleEdit}
            edit_delete_action={["SHIFT_ROTATION_ASSIGNMENT_DELETE", "SHIFT_ROTATION_ASSIGNMENT_EDIT"]}
            token={localStorage.getItem("token")}
            configss={configColumns}
            paginationData={paginationData}
            onSortChange={handleSortChange}
            filterModule="employee_work_shift_rotation_assignments"
            {...(tableConfig && { config: tableConfig })}
          />
      )}
    </>
  );
}

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "300px",
      textAlign: "center",
      color: "text.secondary",
    }}
  >
    <InboxIcon sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      No WorkShift Rotation Assignments Found
    </Typography>
    <Typography variant="body2">
      {window.location.search ? "Try adjusting your filters." : "Add new workshift rotation assignments to get started."}
    </Typography>
  </Box>
);

export default WorkshiftRotationAssignmentList;
